import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, X, Trash2, Loader2, Check, MapPin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BYER = ["København", "Kolding", "Odense", "Aalborg", "Aarhus"];

const EMPTY = { navn: "", adresse: "", by: "København", lat: "", lng: "", radius: 200, aktiv: true };

function LokationModal({ initial, token, onSaved, onClose }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const isEdit = !!initial?.id;

  const handleSave = async () => {
    if (!form.navn.trim() || !form.lat || !form.lng) return;
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/clock-lokationer/${initial.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/clock-lokationer`, form, { headers: { Authorization: `Bearer ${token}` } });
      }
      onSaved();
      onClose();
    } catch { alert("Kunne ikke gemme lokation"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FF2A6D]" />{isEdit ? "Rediger lokation" : "Ny lokation"}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 flex flex-col gap-4">
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Navn *</label>
            <input value={form.navn} onChange={set("navn")} placeholder="fx. Salon København"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Adresse</label>
            <input value={form.adresse} onChange={set("adresse")} placeholder="fx. Bredgade 12, 1260 København"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">By</label>
            <select value={form.by} onChange={set("by")} className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
              {BYER.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Breddegrad (lat) *</label>
              <input type="number" step="any" value={form.lat} onChange={set("lat")} placeholder="55.6761"
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Længdegrad (lng) *</label>
              <input type="number" step="any" value={form.lng} onChange={set("lng")} placeholder="12.5683"
                className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Radius i meter</label>
            <input type="number" value={form.radius} onChange={set("radius")} min="50" max="2000"
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
          </div>
          <div>
            <label className="text-[13px] text-muted-foreground mb-1 block">Status</label>
            <select value={form.aktiv ? "aktiv" : "inaktiv"} onChange={e => setForm(f => ({ ...f, aktiv: e.target.value === "aktiv" }))}
              className="w-full bg-secondary border border-border rounded-lg px-3 h-9 text-sm focus:outline-none">
              <option value="aktiv">Aktiv</option>
              <option value="inaktiv">Inaktiv</option>
            </select>
          </div>
          <p className="text-[13px] text-muted-foreground bg-secondary/50 rounded-lg px-3 py-2">
            Find koordinater på maps.google.com — højreklik på lokationen
          </p>
        </div>
        <div className="flex justify-end gap-2 px-6 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
          <button onClick={handleSave} disabled={saving || !form.navn.trim() || !form.lat || !form.lng}
            className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#FF2A6D] text-white text-sm font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {isEdit ? "Gem ændringer" : "Opret"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LokationerPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [lokationer, setLokationer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [confirmSlet, setConfirmSlet] = useState(null);

  const handleLogout = () => { logout(); navigate("/login"); };

  useEffect(() => {
    if (!user || !["superadmin", "owner", "api_admin"].includes(user.role)) { navigate("/"); return; }
    load();
  }, [user, navigate]);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/clock-lokationer`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLokationer(r.data.lokationer || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleSlet = async (id) => {
    try {
      await axios.delete(`${API}/admin/clock-lokationer/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLokationer(prev => prev.filter(l => l.id !== id));
    } catch { alert("Kunne ikke slette lokation"); }
    setConfirmSlet(null);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />

      {modal !== null && (
        <LokationModal
          initial={modal === "ny" ? null : modal}
          token={token}
          onSaved={load}
          onClose={() => setModal(null)}
        />
      )}

      {confirmSlet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-semibold text-sm mb-2">Slet lokation</h3>
            <p className="text-sm text-muted-foreground mb-5">Er du sikker på at du vil slette "{confirmSlet.navn}"? Dette kan ikke fortrydes.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmSlet(null)} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-secondary transition-colors">Annuller</button>
              <button onClick={() => handleSlet(confirmSlet.id)} className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors">Slet</button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight mb-1">Lokationer</h1>
            <p className="text-sm text-muted-foreground">Administrer clock-lokationer til stempling</p>
          </div>
          <button onClick={() => setModal("ny")}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:bg-[#e02560] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Ny lokation
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <p className="text-[13px] text-muted-foreground mt-3">Henter lokationer...</p>
          </div>
        ) : lokationer.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <MapPin className="w-10 h-10 mb-3 opacity-30" />
            <p className="text-sm">Ingen lokationer oprettet endnu</p>
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  {["Navn", "Adresse", "By", "Radius (m)", "Status", ""].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lokationer.map((l, i) => (
                  <tr key={l.id}
                    className={`border-b border-border hover:bg-secondary/30 transition-colors cursor-pointer ${i % 2 === 0 ? "" : "bg-secondary/10"}`}
                    onClick={() => setModal(l)}>
                    <td className="py-3 px-4 font-medium">{l.navn}</td>
                    <td className="py-3 px-4 text-muted-foreground">{l.adresse || "—"}</td>
                    <td className="py-3 px-4 text-muted-foreground">{l.by || "—"}</td>
                    <td className="py-3 px-4 text-muted-foreground tabular-nums">{l.radius}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-full px-2.5 py-1 ${l.aktiv ? "bg-green-500/10 text-green-500" : "bg-secondary text-muted-foreground"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${l.aktiv ? "bg-green-500" : "bg-muted-foreground"}`} />
                        {l.aktiv ? "Aktiv" : "Inaktiv"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={e => { e.stopPropagation(); setConfirmSlet(l); }}
                        className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
